<template>
  <!-- 底部横幅 -->
  <Transition name="cookie-banner" appear>
    <div
      v-if="showBanner"
      class="fixed bottom-0 left-0 right-0 bg-[#161616] z-[10000] rounded-t-[1.25rem] px-[4.5rem] py-8"
    >
      <div class="mb-8">
        <p
          class="text-white text-base font-normal leading-loose max-w-[75rem] mx-auto text-center"
        >
          We use essential and optional cookies to operate our site, understand
          usage, and tailor your experience. By clicking "Accept All Cookies",
          you agree to our use of optional cookies. You may also decline
          optional cookies or manage your choices
        </p>
      </div>
      <div class="flex items-center justify-center gap-3 shrink-0 w-full">
        <button
          class="cursor-pointer font-medium text-base text-white border border-[#D8D9DC] rounded-[0.625rem] px-4 py-2.5 min-w-[11.25rem] hover:bg-gray-100 hover:text-black transition-colors"
          @click="openDrawer"
        >
          Submit Preference
        </button>
        <button
          class="cursor-pointer font-medium text-base text-white border border-[#D8D9DC] rounded-[0.625rem] px-4 py-2.5 min-w-[8.75rem] hover:bg-gray-100 hover:text-black transition-colors"
          @click="rejectOptionalCookies"
        >
          Decline All
        </button>
        <button
          class="cursor-pointer font-medium text-base text-white bg-blue-500 rounded-[0.625rem] px-4 py-2.5 min-w-[8.75rem] hover:bg-gray-100 hover:text-blue-500 transition-colors"
          @click="acceptAllCookies"
        >
          Accept All
        </button>
      </div>
      <button
        aria-label="Close"
        class="cursor-pointer absolute top-8 right-8"
        @click="closeBanner"
      >
        <icon-close :size="18" style="color: white" />
      </button>
    </div>
  </Transition>

  <!-- 右侧抽屉 -->
  <Transition name="cookie-drawer" appear>
    <div v-if="showDrawer" class="fixed inset-0 z-[10001] pointer-events-none">
      <div
        class="absolute inset-0 bg-black/40 pointer-events-auto"
        @click="closeDrawer"
      />
      <!-- 抽屉内容 -->
      <div
        class="cookie-drawer-panel flex flex-col p-[3.25rem] absolute top-0 right-0 bottom-0 min-w-[40.5rem] w-[40%] max-w-[90vw] bg-[#f3f3f3] rounded-l-[1.25rem] shadow-2xl overflow-y-auto pointer-events-auto"
      >
        <div class="flex-1">
          <!-- 标题 -->
          <h2 class="text-[1.375rem] font-medium mb-[25px] text-blue-500">
            Cookie Preferences
          </h2>

          <!-- 介绍文字 -->
          <p
            class="text-[#5f6368] text-base font-normal mb-[35px] leading-relaxed"
          >
            We use cookies to ensure our website functions properly and to
            improve your experience through analytics and customization. You can
            manage your preferences below.
          </p>

          <!-- Strictly Necessary Cookies -->
          <div
            class="mb-[35px] bg-white border border-[#D8D9DC] rounded-xl p-[26px]"
          >
            <h3 class="text-[1.125rem] font-medium text-[#202124] mb-2">
              Strictly Necessary Cookies
            </h3>
            <p class="text-[#202124] text-[1.125rem] font-medium mb-2">
              (Always Active)
            </p>
            <p class="text-[#5f6368] text-base font-normal leading-loose">
              Essential for security, authentication, and basic functionality.
            </p>
          </div>

          <!-- Enable Optional Cookies -->
          <div
            class="mb-[35px] bg-white border border-[#D8D9DC] rounded-xl p-[26px]"
          >
            <h3 class="text-[18px] font-medium text-[#202124] mb-2">
              Enable Optional Cookies
            </h3>
            <p class="text-[#202124] text-[18px] font-medium mb-[25px]">
              (Analytics & Functional)
            </p>

            <!-- Personalization -->
            <div
              v-if="optionalCookieOptions[0]"
              class="mb-[25px] cursor-pointer"
              @click="toggleCookieOption(optionalCookieOptions[0])"
            >
              <div class="flex items-baseline">
                <div
                  class="w-4 h-4 border border-blue-500 rounded relative"
                  :class="{ 'bg-blue-500': optionalCookieOptions[0]?.enabled }"
                >
                  <span
                    v-if="optionalCookieOptions[0]?.enabled"
                    class="text-white text-xs absolute top-0 left-[50%] translate-x-[-50%]"
                  >
                    √
                  </span>
                </div>
                <div class="ml-3 flex-1">
                  <p
                    class="text-[18px] font-medium mb-1"
                    :class="{
                      'text-blue-500': optionalCookieOptions[0]?.enabled,
                      'text-[#202124]': !optionalCookieOptions[0]?.enabled,
                    }"
                  >
                    Personalization
                  </p>
                  <p class="text-[#5f6368] text-base font-normal">
                    Enables tailored experiences, remembering your preferences,
                    and providing customized content and recommendations.
                  </p>
                </div>
              </div>
            </div>

            <!-- Statistics -->
            <div
              v-if="optionalCookieOptions[1]"
              class="cursor-pointer"
              @click="toggleCookieOption(optionalCookieOptions[1])"
            >
              <div class="flex items-baseline">
                <div
                  class="w-4 h-4 border border-blue-500 rounded relative"
                  :class="{ 'bg-blue-500': optionalCookieOptions[1]?.enabled }"
                >
                  <span
                    v-if="optionalCookieOptions[1]?.enabled"
                    class="text-white text-xs absolute top-0 left-[50%] translate-x-[-50%]"
                  >
                    √
                  </span>
                </div>
                <div class="ml-3 flex-1">
                  <p
                    class="text-[18px] font-medium mb-1"
                    :class="{
                      'text-blue-500': optionalCookieOptions[1]?.enabled,
                      'text-[#202124]': !optionalCookieOptions[1]?.enabled,
                    }"
                  >
                    Statistics
                  </p>
                  <p class="text-[#5f6368] text-base font-normal">
                    Allows us to collect anonymous data about how our services
                    are used, helping us improve performance and features.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 按钮组 -->
        <div class="flex flex-wrap gap-3 justify-end">
          <button
            class="font-medium cursor-pointer text-base text-[#202124] bg-white border border-[#D8D9DC] rounded-[0.625rem] px-4 py-2.5 min-w-[11.25rem] hover:bg-[#D8D9DC] transition-colors"
            @click="savePreferences"
          >
            Submit Preference
          </button>
          <button
            class="font-medium cursor-pointer text-base text-[#202124] bg-white border border-[#D8D9DC] rounded-[0.625rem] px-4 py-2.5 min-w-[8.75rem] hover:bg-[#D8D9DC] transition-colors"
            @click="rejectOptionalCookies"
          >
            Decline All
          </button>
          <button
            class="font-medium cursor-pointer text-base text-white bg-blue-500 rounded-[0.625rem] px-4 py-2.5 min-w-[8.75rem] hover:bg-[#D8D9DC] hover:text-blue-500 transition-colors"
            @click="acceptAllCookies"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { ref, reactive, onMounted, onBeforeUnmount } from 'vue';

  const showBanner = ref(false);
  const showDrawer = ref(false);

  // 定义可选Cookie选项
  interface CookieOption {
    id: string;
    title: string;
    description: string;
    enabled: boolean;
  }

  const optionalCookieOptions = reactive<CookieOption[]>([
    {
      id: 'personalization',
      title: 'Personalization',
      description:
        'Enables tailored experiences, remembering your preferences, and providing customized content and recommendations.',
      enabled: false,
    },
    {
      id: 'statistics',
      title: 'Statistics',
      description:
        'Allows us to collect anonymous data about how our services are used, helping us improve performance and features.',
      enabled: false,
    },
  ]);

  const handleExternalOpen = () => {
    // eslint-disable-next-line no-use-before-define
    showCookiePreferences();
  };

  onMounted(() => {
    // 检查是否已经设置了Cookie偏好
    const cookiePreferencesSet = localStorage.getItem('cookiePreferencesSet');
    if (!cookiePreferencesSet) {
      showBanner.value = true;
    }
    // 加载已保存的可选Cookie选项
    try {
      const savedOptions = localStorage.getItem('optionalCookiesEnabled');
      if (savedOptions) {
        const enabledOptions = JSON.parse(savedOptions) as string[];
        optionalCookieOptions.forEach((option) => {
          option.enabled = enabledOptions.includes(option.id);
        });
      }
    } catch (e) {
      console.error('Error loading saved cookie preferences', e);
    }

    window.addEventListener('open-cookie-preferences', handleExternalOpen);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('open-cookie-preferences', handleExternalOpen);
  });

  // 打开右侧抽屉
  const openDrawer = () => {
    showBanner.value = false;
    showDrawer.value = true;
  };

  // 关闭右侧抽屉
  const closeDrawer = () => {
    showDrawer.value = false;
  };

  // 关闭底部横幅
  const closeBanner = () => {
    showBanner.value = false;
    // 如果关闭横幅，也设置一个标记，避免再次显示
    // localStorage.setItem("cookieBannerDismissed", "true");
  };

  // 暴露方法，允许外部组件显示Cookie偏好设置
  const showCookiePreferences = () => {
    showBanner.value = true;
  };

  // 暴露组件内部方法给外部使用
  defineExpose({
    showCookiePreferences,
  });

  // 切换单个Cookie选项
  const toggleCookieOption = (option: CookieOption | undefined) => {
    if (option) {
      option.enabled = !option.enabled;
    }
  };

  const setCookiePreferences = () => {
    // 记录已经设置了Cookie偏好
    localStorage.setItem('cookiePreferencesSet', 'true');

    // 保存启用的可选Cookie ID数组
    const enabledOptions = optionalCookieOptions
      .filter((opt) => opt.enabled)
      .map((opt) => opt.id);

    localStorage.setItem(
      'optionalCookiesEnabled',
      JSON.stringify(enabledOptions)
    );
  };

  const savePreferences = () => {
    setCookiePreferences();
    showDrawer.value = false;
    showBanner.value = false;
  };

  const acceptAllCookies = () => {
    optionalCookieOptions.forEach((opt) => {
      opt.enabled = true;
    });
    setCookiePreferences();
    showDrawer.value = false;
    showBanner.value = false;
  };

  const rejectOptionalCookies = () => {
    optionalCookieOptions.forEach((opt) => {
      opt.enabled = false;
    });
    setCookiePreferences();
    showDrawer.value = false;
    showBanner.value = false;
  };
</script>

<style scoped>
  .cookie-banner-enter-active,
  .cookie-banner-leave-active {
    transition: transform 0.4s ease, opacity 0.4s ease;
  }

  .cookie-banner-enter-from,
  .cookie-banner-leave-to {
    transform: translateY(100%);
    opacity: 0;
  }

  .cookie-drawer-enter-active,
  .cookie-drawer-leave-active {
    transition: opacity 0.4s ease;
  }

  .cookie-drawer-enter-from,
  .cookie-drawer-leave-to {
    opacity: 0;
  }

  .cookie-drawer-enter-from .cookie-drawer-panel,
  .cookie-drawer-leave-to .cookie-drawer-panel {
    transform: translateX(100%);
  }

  .cookie-drawer-enter-active .cookie-drawer-panel,
  .cookie-drawer-leave-active .cookie-drawer-panel {
    transition: transform 0.4s ease;
  }
</style>
